import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { omit } from 'lodash';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Input,
  InputDecoration,
  InputGroup,
  Separator,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { useQuery } from '@tanstack/react-query';
import { ArrowUp, Check, ChevronDown, Plus, Search, Settings2 } from 'lucide-react';
import { agentQueryOptions, getAgents, SPECIAL_AGENT_IDS } from '@/entities/agent';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { useChatInput, useChatProjectContextPicker } from '@/features/chats';
import { getModelDisplayLabel } from '@/features/chats/lib/getModelDisplayLabel';
import { getFileAcceptString } from '@/features/chats/model/file-upload.constants';
import {
  hasVideoCapableModel,
  useVideoAttachmentsEnabled,
} from '@/features/chats/model/useVideoAttachmentsEnabled';
import { useModelCompatibilityGuard } from '@/features/chats/model/useModelCompatibilityGuard';
import { isVideoCapableModel } from '@inference.engine/model-registry/web';
import { useAgentModelAvailability } from '@/features/chats/model/useAgentModelAvailability';
import { persistTemplatesSettingsFromStore } from '@/features/chats/model/useChatSettingsPersistence';
import { FileStatus } from '@/features/chats/model/useFileAttachments/useFileAttachments.types';
import {
  useChatActions,
  useChatId,
  useIsCurrentAgentUnavailable,
  useIsSwitching,
} from '@/features/chats/stores';
import { getChatStores } from '@/features/chats/lib/chat-store-bindings';
import { TOOL_BADGE_HUES, ToolBadge, toToolBadgeCategory } from '@/shared/ui/ToolBadge';
import { UnsavedChangesModal } from '@/shared/ui/UnsavedChangesModal';
import { useKnowledgeBasesList } from '@/shared/hooks';
import { AgentDeletedNotice } from '../AgentDeletedNotice';
import { ChatAlert } from '../ChatAlert';
import { EffortControl, effortLevelsFor } from '../EffortControl';
import { useFileValidation } from '../ChatDropzone/hooks/useFileValidation';
import { ChatAddMenu } from '../ChatAddMenu';
import { ChatSuggestionsToggle } from '../ChatSuggestionsToggle';
import { ChatInputExtensions } from '../ChatInputExtensions/ChatInputExtensions';
import { ChatGuidelinesSelectorV2 } from '../ChatOptions/ChatGuidelinesSelector/ChatGuidelinesSelectorV2';
import { ChatKnowledgeBaseSelectorV2 } from '../ChatOptions/ChatKnowledgeBaseSelector';
import { ChatOptions } from '../ChatOptions/ChatOptions';
import { chatOptionsFor } from '../ChatOptions/ChatOptions.registry';
import { ImageModelChip } from '../ChatOptions/ImageGeneration/ImageModelChip';
import type { ImageModel } from '../ChatOptions/ImageGeneration/ImageGenerationOptions.types';
import { getOptionsForModel as getImageDefaultOptions } from '../ChatOptions/ImageGeneration/ImageOptionsHelpers';
import { useImageSeed } from '../ChatOptions/ImageGeneration/useImageSeed';
import type { VideoModel } from '../ChatOptions/VideoGeneration/VideoGenerationOptions.types';
import { getOptionsForModel as getVideoDefaultOptions } from '../ChatOptions/VideoGeneration/videoOptionsHelpers';
import { ChatInputProps } from './ChatInput.types';
import './ChatInput.styles.css';
import { getChatMcp } from '@/lib/chat-mcp';
import { useTranslation } from 'react-i18next';

// Allowed tools to display in the switcher
/* eslint-disable i18next/no-literal-string -- matched against `agent.title` from the API, not rendered */
const ALLOWED_TOOLS = [
  'Strategy',
  'Audience',
  'Email',
  'Email 2.0',
  'Image',
  'Copy',
  'Video',
  'Ideation',
  'Translation',
  'Quality assurance',
];
/* eslint-enable i18next/no-literal-string */

// Agents that only have model selection (no other options)
// Since model is shown on the right, hide settings for these agents.
// Sentinel exposes guidelines as a first-class button in the configuration
// bar, so its settings popup would otherwise render empty.
const AGENTS_WITH_ONLY_MODEL_OPTIONS = ['strategy-agent-v3', 'sentinel-buddy-agent'];

// These agents keep their model in `settings` (not `llmOptions`). Their popups
// build the full settings payload via getOptionsForModel; the chip/seed do too.
const AGENTS_WITH_MODEL_IN_SETTINGS = ['image-generation', 'video-generation'];

const buildSettingsOptionsForModel = (
  agentId: string,
  model: string,
): Record<string, unknown> | null => {
  if (agentId === 'image-generation') {
    return getImageDefaultOptions(model as ImageModel | 'custom-workflow') as unknown as Record<
      string,
      unknown
    >;
  }
  if (agentId === 'video-generation') {
    return getVideoDefaultOptions(model as VideoModel) as unknown as Record<string, unknown>;
  }
  return null;
};

/** The effort a model runs at: `previous` when it takes that level, else its recommended one. */
function effortOn(model: string, previous: unknown): string | undefined {
  const support = effortLevelsFor(model);
  if (!support) return undefined;
  return support.levels.find((level) => level === previous) ?? support.recommended;
}

export const ChatInput = memo((props: ChatInputProps) => {
  const { t } = useTranslation('chats');

  const {
    isNewChat,
    onSend,
    onStop,
    sendBlockedReason,
    busyLabel,
    footerEnd,
    inputTop,
    debugControls,
  } = props;
  const { ProjectContextPicker, useProjectContextRows } = props.projectContextDeps;
  // Set once the agent's card is known, below; read at send time.
  const imageAtDefaults = useRef<(inputs: Record<string, unknown>) => boolean>(() => false);
  const sendPicked = useCallback<typeof onSend>(
    (input) =>
      onSend(
        imageAtDefaults.current(input.inputs ?? {}) ? { ...input, imageAtDefaults: true } : input,
      ),
    [onSend],
  );
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [optionsInternalLoading, setOptionsInternalLoading] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // The settings menu dismisses on window blur, which is exactly what opening a native file
  // dialog does. Holding it open for the duration keeps the picker's own input mounted, so the
  // file the user chooses still reaches a live handler.
  const filePickerOpen = useRef(false);
  const handleSettingsOpenChange = useCallback((open: boolean) => {
    if (!open && filePickerOpen.current) return;
    setSettingsOpen(open);
  }, []);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [toolSwitcherOpen, setToolSwitcherOpen] = useState(false);
  const [toolSearch, setToolSearch] = useState('');

  // Agent switching state for immediate visual feedback
  const isAgentSwitching = useIsSwitching();
  const isAgentUnavailable = useIsCurrentAgentUnavailable();
  const chatId = useChatId();
  const { setAgentById } = useChatActions();

  const {
    currentAgentId,
    displayAgent,
    files,
    input,
    isConnected,
    isAiBusy,
    isWaiting,
    isUploading,
    isCurrentAgentBusy,
    isOtherAgentBusy,
    options,
    handleInputChange,
    handleSendMessageInternal,
    handleInputFileUpload,
    handleRemoveFile,
    handleFileUpload,
    updateOptions,
    unsavedEditsPromptOpen,
    confirmSendWithUnsavedEdits,
    cancelSendWithUnsavedEdits,
  } = useChatInput({ isNewChat, onSend: sendPicked });

  const workspaceId = useCurrentWorkspaceId();

  const pasteFilesEnabled = useBooleanFlagValue('paste_files_into_chat', false);
  const videoAttachmentsEnabled = useVideoAttachmentsEnabled();
  const { getPartitionedFiles, getValidFiles, notifyRejectedFiles } =
    useFileValidation(currentAgentId);

  // Fetch all agents for the switcher
  const { data: allAgents = [] } = useQuery({
    queryKey: ['agents', workspaceId],
    queryFn: () => getAgents(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Filter agents to only show allowed tools
  const filteredAgents = useMemo(
    () => allAgents.filter((agent) => ALLOWED_TOOLS.includes(agent.title)),
    [allAgents],
  );

  const visibleAgents = useMemo(() => {
    const userQuery = toolSearch.trim().toLowerCase();
    if (!userQuery) return filteredAgents;
    return filteredAgents.filter((agent) => agent.title.toLowerCase().includes(userQuery));
  }, [filteredAgents, toolSearch]);

  const { data: knowledgeBasesData, status: kbStatus } = useKnowledgeBasesList();
  const knowledgeBases = useMemo(() => knowledgeBasesData ?? [], [knowledgeBasesData]);
  const knowledgeBasesLoading = kbStatus === 'pending';

  type KBItem = { id: string; name: string };
  const selectableKBs = useMemo(
    () =>
      knowledgeBases
        .reduce<KBItem[]>((acc, kb) => {
          if (kb.id && kb.name && !kb.isInternal) acc.push({ id: kb.id, name: kb.name });
          return acc;
        }, [])
        .sort((a, b) => a.name.localeCompare(b.name)),
    [knowledgeBases],
  );

  // Gate by agent capability
  const showKnowledgeBaseSelector = useMemo(() => {
    const supportsKnowledgeBases = displayAgent?.capabilities?.knowledgeBases ?? false;
    return supportsKnowledgeBases && selectableKBs.length > 0;
  }, [displayAgent?.capabilities?.knowledgeBases, selectableKBs.length]);

  // Block interaction during waiting, switching, file upload, or deleted agent
  const canUserInteract =
    !isAgentUnavailable &&
    ((isNewChat && !!displayAgent && !isAiBusy) || (!isWaiting && !isAgentSwitching));

  // Check if current agent has models available (if it requires them)
  const { canUseAgent } = useAgentModelAvailability();

  // Check if agent tools are configured
  const isAgentConfigured = displayAgent?.configurationStatus?.configured !== false;

  const hasReadyFiles = useMemo(
    () =>
      files.some(
        (file) => file.status === FileStatus.UPLOADED || file.status === FileStatus.REFERENCED,
      ),
    [files],
  );

  // The model currently selected in the composer. Agents that keep the model in
  // `settings` (image/video generation) differ from those that keep it in `llmOptions`.
  const selectedModel = useMemo(() => {
    if (AGENTS_WITH_MODEL_IN_SETTINGS.includes(currentAgentId)) {
      return (options?.settings as { model?: string } | undefined)?.model;
    }
    return (options?.llmOptions as { model?: string } | undefined)?.model;
  }, [currentAgentId, options]);

  // The builder's debug chat lets an admin try an agent that offers no choice on any model it can
  // run; an agent that offers some keeps to them, as the run does.
  const offeredModels = displayAgent?.enabledModels?.length
    ? displayAgent.enabledModels
    : (debugControls?.models ?? displayAgent?.enabledModels);

  const hasVideoAttachment = useMemo(
    () => files.some((file) => file.mimeType.startsWith('video/')),
    [files],
  );

  // A video attached to a non-video model blocks send. Normally the compatibility
  // guard (below) auto-switches to a video-capable model, but when the agent has no
  // video-capable model enabled there is nothing to switch to, so we must stop the
  // request here rather than let it fail server-side.
  const videoModelIncompatible =
    hasVideoAttachment && !!selectedModel && !isVideoCapableModel(selectedModel);

  const canSendMessage =
    !sendBlockedReason &&
    canUserInteract &&
    !isUploading &&
    !optionsInternalLoading &&
    canUseAgent &&
    isAgentConfigured &&
    !videoModelIncompatible &&
    (input.trim().length > 0 || hasReadyFiles);

  // Send-button tooltip. When a video blocks send, the compatibility ChatAlert may
  // already be dismissed, so the tooltip has to stand on its own: say why send is
  // blocked and what resolves it. With no video-capable model enabled, every entry
  // in the model picker is greyed out too, so "switch models" would be a dead end.
  const sendTooltip = useMemo(() => {
    if (sendBlockedReason) return sendBlockedReason;
    if (!videoModelIncompatible) return t('chats.chatInputV2.sendMessage');
    return hasVideoCapableModel(offeredModels)
      ? t('chats.chatInputV2.videoNeedsGoogleModel')
      : t('chats.chatInputV2.videoNeedsGoogleModelNoneEnabled');
  }, [sendBlockedReason, videoModelIncompatible, offeredModels, t]);

  const busyText = busyLabel ?? t('chats.chatInputV2.thinking');

  // Placeholder
  const placeholder = useMemo(() => {
    if (isAgentUnavailable) return t('chats.chatInput.placeholderToolUnavailable');
    if (sendBlockedReason) return sendBlockedReason;
    if (isAgentSwitching) return t('chats.chatInput.placeholderSwitchingAgent');
    if (isUploading) return t('chats.chatInput.placeholderUploading');
    if (!displayAgent) return t('chats.intro.loading');
    if (!isAgentConfigured) return t('chats.chatInput.placeholderNeedsConfiguration');
    if (isCurrentAgentBusy) return busyText;
    if (isOtherAgentBusy) return t('chats.chatInput.placeholderOtherAgentBusy');
    if (isConnected)
      return displayAgent.placeholderPrompt || t('chats.chatInput.placeholderAskAnything');
    return t('chats.chatInput.placeholderConnecting');
  }, [
    displayAgent,
    isAgentConfigured,
    isAgentUnavailable,
    isAgentSwitching,
    isConnected,
    isCurrentAgentBusy,
    isOtherAgentBusy,
    isUploading,
    sendBlockedReason,
    busyText,
    t,
  ]);

  const onTextAreaKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (canSendMessage) {
          handleSendMessageInternal();
        }
      }
    },
    [handleSendMessageInternal, canSendMessage],
  );

  const onTextAreaPaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      if (!pasteFilesEnabled || !canUserInteract) return;
      const { clipboardData } = e;
      const [accepted, rejected] = getPartitionedFiles(clipboardData);
      if (accepted.length === 0) return;
      e.preventDefault();
      notifyRejectedFiles(rejected);
      const validFiles = getValidFiles(accepted);
      if (validFiles.length > 0) void handleFileUpload(validFiles);
    },
    [
      pasteFilesEnabled,
      canUserInteract,
      getPartitionedFiles,
      getValidFiles,
      notifyRejectedFiles,
      handleFileUpload,
    ],
  );

  // A persona is an invisible sub-agent of Audience carrying a hardcoded `tag`, so its own
  // category would stay put after an admin moved Audience. Take the parent's instead. Fetched
  // only for a persona, so every other agent keeps reading the tag it already has.
  const isPersonaAgent =
    displayAgent?.id.startsWith(SPECIAL_AGENT_IDS.AUDIENCE_FLOW_PERSONA_PREFIX) ?? false;
  const { data: audienceAgent } = useQuery({
    ...agentQueryOptions(SPECIAL_AGENT_IDS.AUDIENCE_FLOW, workspaceId),
    enabled: isPersonaAgent,
  });

  // The focus border and ring take the same themed hue as the agent's badge in the toolbar.
  const categoryTag = isPersonaAgent ? audienceAgent?.tag : displayAgent?.tag;
  const categoryText = `var(--badge-${TOOL_BADGE_HUES[toToolBadgeCategory(categoryTag)]}-foreground)`;
  const focusRingColor = `color-mix(in srgb, ${categoryText} 20%, transparent)`;

  // Handle agent switching. Switch the active agent in-place and stay in the
  // current chat — including when leaving the audience interview/persona shell.
  // The shell swap this triggers is handled by `prepareChatHistory`'s guard
  // (see `useAiChat`), which keeps the deliberate switch instead of snapping
  // back to the chat's previous agent/widget.
  const handleAgentSwitch = useCallback(
    async (agentId: string) => {
      if (agentId === displayAgent?.id) return;

      // User-initiated switch: reset the widget side panel first. Without
      // this, the audience widget panel
      // stays open when switching to an agent that has no widget of its own.
      getChatStores().widgetStore.useWidgetStore.getState().clearWidgetPanel();

      await setAgentById(agentId);
    },
    [displayAgent?.id, setAgentById],
  );

  // Determine if we should show stop button
  const shouldShowStop = isAiBusy && isConnected && onStop;
  const shouldShowSend = !shouldShowStop;

  // Track whether ChatInputExtensions has content (suggestions or files)
  const [hasExtensions, setHasExtensions] = useState(false);
  const handleHasContentChange = useCallback((hasContent: boolean) => {
    setHasExtensions(hasContent);
  }, []);

  // Check if current agent has custom input options
  // Hide settings button for agents that only have model selection (shown on the right)
  const shouldShowChatOptions =
    chatOptionsFor(displayAgent ?? { id: currentAgentId }) !== undefined &&
    !AGENTS_WITH_ONLY_MODEL_OPTIONS.includes(currentAgentId);

  // MCP servers assigned to this agent, resolved to live connection status + composer UI slots.
  // Read through the @/lib/chat-mcp seam so chats doesn't value-import features/user-mcp-servers
  // (FSD layer order). When the flag is off or the agent has none, this is inert (no MCP UI).
  // Keyed on the card's id: a chat opened on a legacy id is served the card under its current one.
  const {
    hasMcps: hasAgentMcps,
    needsAttention: mcpNeedsAttention,
    connectorsSlot: mcpConnectorsSlot,
    modalSlot: mcpModalSlot,
  } = getChatMcp().useChatAgentMcp(displayAgent?.id, {
    onCloseMenu: () => setAddMenuOpen(false),
  });

  // The settings dropdown is now only for custom chat inputs (MCPs moved to the + Add menu).
  const showSettings = shouldShowChatOptions;

  // Gate the whole project-context feature. Flag off → "+" opens the file
  // dialog directly (legacy); flag on → "+" opens the add-menu.
  const isProjectContextEnabled = useBooleanFlagValue('project_context_attachment', false);

  // The + Add menu also hosts the agent's "Connectors" (MCP servers), so show it whenever the
  // agent has MCPs assigned — even if project-context attachment is off.
  const showAddMenu = isProjectContextEnabled || hasAgentMcps;

  // Handle file upload
  const handleFileButtonClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Project-context picker: stored-selection narrowing, controlled modal
  // state, and the add / remove / confirm handlers. The rows hook is injected
  // so `features/chats` stays free of `@/features/add-project-context`.
  const {
    projectContextItems,
    projectContextCount,
    isPickerOpen,
    setIsPickerOpen,
    handleAddProjectContext,
    handleRemoveProjectContextItem,
    handleConfirmProjectContext,
    canConfirm,
    selectedCount,
    pickerProps,
  } = useChatProjectContextPicker({ options, updateOptions, useProjectContextRows });

  const handleFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      void handleInputFileUpload(event);
    },
    [handleInputFileUpload],
  );

  const acceptedFileTypes = useMemo(
    () => getFileAcceptString(currentAgentId, videoAttachmentsEnabled),
    [currentAgentId, videoAttachmentsEnabled],
  );

  // Knowledge base selection
  const selectedKnowledgeBaseIds: string[] = useMemo(() => {
    const maybe = options?.knowledgeBaseIds;
    return Array.isArray(maybe) ? (maybe as string[]) : [];
  }, [options]);

  // Guidelines selection (gated by agent capability — typically Sentinel/QA)
  const showGuidelinesSelector = displayAgent?.capabilities?.guidelines ?? false;

  const selectedGuidelineIds: string[] = useMemo(() => {
    const maybe = options?.selectedGuidelineIds;
    return Array.isArray(maybe) ? (maybe as string[]) : [];
  }, [options]);

  const handleGuidelineChange = useCallback(
    (ids: string[]) => {
      updateOptions({
        ...(options ?? {}),
        selectedGuidelineIds: ids,
      });
      persistTemplatesSettingsFromStore(); // user change → persist (no-op for non-templates)
    },
    [options, updateOptions],
  );

  // The API applies a per-message effort only on debug turns, in place of the agent's own.
  const effortEnabled = debugControls !== undefined;
  const agentEffort = debugControls?.effort;
  const currentEffort = (options?.llmOptions as { effort?: unknown } | undefined)?.effort;

  // Model selection — image/video keep their model in `settings`, all other
  // agents in `llmOptions`.
  const handleModelChange = useCallback(
    (model: string) => {
      const settingsOptions = buildSettingsOptionsForModel(currentAgentId, model);
      if (settingsOptions) {
        // image/video: write the full settings payload, never llmOptions
        updateOptions(settingsOptions);
        return;
      }
      const { effort: previous, ...llmOptions } = (options?.llmOptions ?? {}) as Record<
        string,
        unknown
      >;
      const effort = effortEnabled ? effortOn(model, previous ?? agentEffort) : previous;
      updateOptions({
        ...(options ?? {}),
        llmOptions: { ...llmOptions, model, ...(effort ? { effort } : {}) },
      });
      persistTemplatesSettingsFromStore(); // user change → persist (no-op for non-templates)
    },
    [currentAgentId, options, updateOptions, effortEnabled, agentEffort],
  );

  // What the effort control shows is what is sent, so a model that reasons starts at the agent's
  // level, or its recommended one, rather than at whatever the provider defaults to.
  const effortToSeed =
    effortEnabled && selectedModel && !AGENTS_WITH_MODEL_IN_SETTINGS.includes(currentAgentId)
      ? effortOn(selectedModel, currentEffort ?? agentEffort)
      : undefined;
  useEffect(() => {
    if (!options || !effortToSeed || effortToSeed === currentEffort) return;
    updateOptions({
      ...options,
      llmOptions: { ...(options.llmOptions ?? {}), effort: effortToSeed },
    });
  }, [options, effortToSeed, currentEffort, updateOptions]);

  // Keep the selected model compatible with attachments: if a video is attached
  // to a non-Google model, auto-switch to a video-capable model and surface an
  // informational ChatAlert. `selectedModel` / `hasVideoAttachment` are derived
  // above so the send gate can also block on incompatibility.
  //
  // Note this routes through `handleModelChange`, so on the templates agent the
  // auto-switch is persisted like any user model change — deliberately, since
  // storage is authoritative and would otherwise revert it. See
  // `persistTemplatesSettingsFromStore`'s doc before "fixing" that.
  const { alert: compatibilityAlert, dismissAlert: dismissCompatibilityAlert } =
    useModelCompatibilityGuard({
      agentId: currentAgentId,
      sessionId: chatId ?? undefined,
      hasVideoAttachment,
      selectedModel,
      enabledModels: offeredModels,
      onModelChange: handleModelChange,
    });

  // Seed default options on mount for agents whose <ChatOptions> popup is hidden
  // or lazy-mounted in V2 (model lives in the right-side chip). Without this,
  // sending the first message before touching the chip leaves options undefined.
  // image/video seed their full `settings` payload; other agents seed
  // `llmOptions.model` as before.
  const defaultModel = offeredModels?.[0];
  useEffect(() => {
    if (options || !defaultModel || displayAgent?.id !== currentAgentId) return;
    const settingsOptions = buildSettingsOptionsForModel(currentAgentId, defaultModel);
    updateOptions(settingsOptions ?? { llmOptions: { model: defaultModel } });
  }, [options, defaultModel, updateOptions, displayAgent?.id, currentAgentId]);
  // After the chat model's seed, which replaces the options, so the image defaults merge into it.
  const imageGeneration = displayAgent?.capabilities?.imageGeneration;
  const imageModel = (options?.settings as { model?: string } | undefined)?.model;
  const { atSeed, pickModel } = useImageSeed(imageGeneration?.defaults, options, updateOptions);
  useEffect(() => {
    imageAtDefaults.current = atSeed;
  }, [atSeed]);

  // The Test chat starts on the agent's own model and effort, and follows them when the admin
  // changes the agent between tests, while it still shows what the old setting seeded. A value
  // picked here differs from the seed and stays. Keying the chat on them would remount it and drop
  // the conversation.
  const seededModel = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!debugControls || !defaultModel || displayAgent?.id !== currentAgentId) return;
    const before = seededModel.current;
    seededModel.current = defaultModel;
    if (before && before !== defaultModel && selectedModel === before) {
      handleModelChange(defaultModel);
    }
  }, [
    debugControls,
    defaultModel,
    displayAgent?.id,
    currentAgentId,
    selectedModel,
    handleModelChange,
  ]);
  const seededEffort = useRef(agentEffort);
  useEffect(() => {
    if (!debugControls || !options || !selectedModel) return;
    const before = seededEffort.current;
    if (before === agentEffort) return;
    seededEffort.current = agentEffort;
    if (currentEffort !== effortOn(selectedModel, before)) return;
    const effort = effortOn(selectedModel, agentEffort);
    updateOptions({
      ...options,
      llmOptions: { ...omit(options.llmOptions ?? {}, 'effort'), ...(effort ? { effort } : {}) },
    });
  }, [debugControls, options, selectedModel, agentEffort, currentEffort, updateOptions]);

  // A chat still holding a model the agent no longer offers, one dropped from the curated list,
  // moves to its default, so the chip never shows a model its menu does not list. Image and video
  // keep their own model lists, custom workflows included, so their popups handle this.
  useEffect(() => {
    if (!selectedModel || !defaultModel || displayAgent?.id !== currentAgentId) return;
    if (AGENTS_WITH_MODEL_IN_SETTINGS.includes(currentAgentId)) return;
    if (offeredModels.includes(selectedModel)) return;
    handleModelChange(defaultModel);
  }, [selectedModel, defaultModel, displayAgent, offeredModels, currentAgentId, handleModelChange]);

  // A model held from before an agent stopped offering a choice is dropped, not sent: the API runs
  // any model a request names, so the chat keeps a fixed model fixed. The debug chat's list is
  // empty only while its catalogue loads, and its picks are its own.
  useEffect(() => {
    if (
      debugControls ||
      defaultModel ||
      !selectedModel ||
      displayAgent?.id !== currentAgentId ||
      AGENTS_WITH_MODEL_IN_SETTINGS.includes(currentAgentId)
    ) {
      return;
    }
    updateOptions({ ...options, llmOptions: omit(options?.llmOptions ?? {}, 'model') });
  }, [
    debugControls,
    defaultModel,
    selectedModel,
    displayAgent?.id,
    currentAgentId,
    options,
    updateOptions,
  ]);

  return (
    <TooltipProvider>
      <div className="w-full max-w-175.5">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileInputChange}
          accept={acceptedFileTypes}
        />
        {mcpModalSlot}
        {isAgentUnavailable && (
          <div className="mb-2">
            <AgentDeletedNotice />
          </div>
        )}
        {/* Model-compatibility alert sits BEHIND the input (lower z-index) and
            peeks out from the top edge: the negative bottom margin pulls the
            input up over the alert's bottom, and the input's opaque
            `bg-background` + higher z-index hide the tucked portion. The overlap
            (8px) stays below the alert's 16px bottom padding, leaving an 8px gap
            between the alert text and the input's top border. Do not increase it
            to the full 16px — the border would then sit flush against the text. */}
        {compatibilityAlert && (
          <div className="relative z-0 -mb-2">
            <ChatAlert message={compatibilityAlert} onDismiss={dismissCompatibilityAlert} />
          </div>
        )}

        <div className="relative z-10 flex flex-col gap-4">
          {/* Chat Input Container */}
          <div
            className={`bg-background border transition-[border-color,border-radius,box-shadow] duration-base ease-out`}
            style={{
              borderColor: isFocused ? categoryText : 'var(--border-light)',
              borderRadius: hasExtensions ? '20px' : '16px',
              boxShadow: isFocused
                ? `0 0 0 3px ${focusRingColor}, 0 4px 8px rgba(0, 0, 0, 0.1)`
                : '0 4px 8px rgba(0, 0, 0, 0.1)',
            }}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
          >
            {/* Suggestions + message attachments (files and project context) */}
            <ChatInputExtensions
              onSend={sendPicked}
              isNewChat={isNewChat}
              waiting={isAiBusy}
              files={files}
              handleRemoveFile={handleRemoveFile}
              onHasContentChange={handleHasContentChange}
              projectContextItems={isProjectContextEnabled ? projectContextItems : []}
              onRemoveProjectContextItem={handleRemoveProjectContextItem}
              {...(inputTop ? { top: inputTop } : {})}
            />

            <div className="relative flex items-center gap-2 p-4">
              {/* Tool Icon Button (Agent Switcher) */}
              <div>
                <DropdownMenu
                  open={toolSwitcherOpen}
                  onOpenChange={(next) => {
                    setToolSwitcherOpen(next);
                    if (!next) setToolSearch('');
                  }}
                >
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="size-8 shrink-0 rounded-sm p-0 hover:bg-transparent"
                          aria-label={t('chats.chatInputV2.changeTool')}
                          aria-description={displayAgent?.title}
                          disabled={!canUserInteract}
                        >
                          {displayAgent && (
                            <ToolBadge
                              toolId={displayAgent.id}
                              category={categoryTag}
                              size="sm"
                              glyphClassName="toolbar-icon-hover"
                            />
                          )}
                        </Button>
                      </DropdownMenuTrigger>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{displayAgent?.title || t('chats.chatInputV2.changeTool')}</p>
                    </TooltipContent>
                  </Tooltip>

                  <DropdownMenuContent align="start" className="w-64 overflow-hidden">
                    <DropdownMenuLabel className="p-2">
                      <p>{t('chats.chatInputV2.changeTool')}</p>
                    </DropdownMenuLabel>
                    <div className="px-2 pb-2">
                      <InputGroup
                        size="sm"
                        className="w-full border-0 has-[input:focus-visible]:ring-0 has-[input:focus-visible]:ring-offset-0"
                      >
                        <InputDecoration muted>
                          <Search className="size-3.5" />
                        </InputDecoration>
                        <Input
                          placeholder={t('chats.chatInputV2.search')}
                          value={toolSearch}
                          onChange={(e) => setToolSearch(e.target.value)}
                          onKeyDown={(e) => e.stopPropagation()}
                          aria-label={t('chats.chatInputV2.searchTools')}
                        />
                      </InputGroup>
                    </div>
                    <div className="max-h-62.5 overflow-y-auto pb-1">
                      {visibleAgents.length === 0 ? (
                        <div className="px-3 py-4 text-center text-xs text-foreground-subtle">
                          {t('chats.chatInputV2.noTools')}
                        </div>
                      ) : (
                        visibleAgents.map((agent) => {
                          const isCurrentAgent = agent.id === displayAgent?.id;
                          return (
                            <DropdownMenuItem
                              key={agent.id}
                              className="hover:bg-surface! hover:text-foreground!"
                              onClick={() => {
                                void handleAgentSwitch(agent.id);
                                setToolSwitcherOpen(false);
                              }}
                            >
                              <div className="flex w-full items-center gap-2">
                                <ToolBadge toolId={agent.id} category={agent.tag} size="xs" />
                                <span className="flex-1 truncate">{agent.title}</span>
                                {isCurrentAgent && <Check className="h-4 w-4" />}
                              </div>
                            </DropdownMenuItem>
                          );
                        })
                      )}
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Text Input */}
              <div className="relative flex-1 min-w-0">
                <Textarea
                  ref={textareaRef}
                  aria-label={t('chats.chatInputV2.chatInput')}
                  value={input}
                  onChange={handleInputChange}
                  onKeyDown={onTextAreaKeyDown}
                  onPaste={onTextAreaPaste}
                  placeholder={isCurrentAgentBusy ? '' : placeholder}
                  disabled={!canUserInteract}
                  className="min-h-6 max-h-82.5 shadow-none resize-none border-0 outline-0 focus-visible:ring-0 focus-visible:ring-offset-0 w-full px-1 py-0 text-base  break-words"
                />
                {isCurrentAgentBusy && input.trim().length === 0 && (
                  <div className="absolute inset-0 pointer-events-none flex items-center">
                    <span className="thinking-text">{busyText}</span>
                  </div>
                )}
              </div>

              {/* Send/Stop Button */}
              <div>
                {shouldShowSend && (
                  <Tooltip>
                    {/* A disabled button swallows pointer events, so the wrapping
                        span carries hover — without it the tooltip never opens in
                        exactly the state that most needs explaining. */}
                    <TooltipTrigger asChild>
                      <span className={`inline-flex${canSendMessage ? '' : ' cursor-not-allowed'}`}>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="shrink-0 h-8 w-8 rounded-lg send-button"
                          aria-label={t('chats.chatInputV2.sendMessage')}
                          onClick={handleSendMessageInternal}
                          disabled={!canSendMessage}
                        >
                          <span className="send-button-icon">
                            <ArrowUp className="h-4 w-4" />
                          </span>
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{sendTooltip}</p>
                    </TooltipContent>
                  </Tooltip>
                )}

                {shouldShowStop && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        roundness="full"
                        variant="secondary"
                        size="sm"
                        className="shrink-0 h-8 w-8 p-0! bg-gray-900!"
                        aria-label={t('chats.chatInputV2.stopGenerating')}
                        onClick={onStop}
                      >
                        <span className="bg-background rounded-xs size-3 flex items-center justify-center"></span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{t('chats.chatInputV2.stopGenerating')}</p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            </div>
          </div>
          {/* Configuration Bar */}
          <div className="flex items-center justify-between">
            {/* Left Actions */}
            <div className="flex items-center gap-0.5">
              {/* Add: file dialog directly (legacy) or add-menu (project context and/or Connectors) */}
              {showAddMenu ? (
                <ChatAddMenu
                  open={addMenuOpen}
                  onOpenChange={setAddMenuOpen}
                  onAddFile={handleFileButtonClick}
                  onAddProjectContext={handleAddProjectContext}
                  projectContextCount={projectContextCount}
                  showProjectContext={isProjectContextEnabled}
                  connectorsSlot={hasAgentMcps ? mcpConnectorsSlot : undefined}
                  needsAttention={mcpNeedsAttention}
                  disabled={!canUserInteract}
                />
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 rounded-lg"
                      aria-label={t('chats.chatInputV2.addFiles')}
                      onClick={handleFileButtonClick}
                      disabled={!canUserInteract}
                    >
                      <span className="toolbar-icon-hover">
                        <Plus className="h-4 w-4" />
                      </span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t('chats.chatInputV2.addFiles')}</p>
                  </TooltipContent>
                </Tooltip>
              )}

              <ChatSuggestionsToggle disabled={!canUserInteract} />

              {/* Add Knowledge */}
              {showKnowledgeBaseSelector && (
                <ChatKnowledgeBaseSelectorV2
                  options={selectableKBs}
                  value={selectedKnowledgeBaseIds}
                  onChange={(ids) => {
                    updateOptions({ ...(options ?? {}), knowledgeBaseIds: ids });
                    persistTemplatesSettingsFromStore(); // user change → persist (no-op for non-templates)
                  }}
                  disabled={!canUserInteract}
                  loading={knowledgeBasesLoading}
                />
              )}

              {/* Guidelines selector (Sentinel/QA capability) */}
              {showGuidelinesSelector && (
                <ChatGuidelinesSelectorV2
                  value={selectedGuidelineIds}
                  onChange={handleGuidelineChange}
                  disabled={!canUserInteract}
                />
              )}

              {/* Settings - shown when the agent has custom inputs and/or assigned MCPs */}
              {showSettings && (
                <>
                  {/* Separator */}
                  <div className="flex items-center justify-center h-5 w-px px-0.5">
                    <Separator orientation="vertical" className="h-full" />
                  </div>

                  <DropdownMenu open={settingsOpen} onOpenChange={handleSettingsOpenChange}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="relative h-8 w-8 rounded-lg"
                            aria-label={t('chats.chatInputV2.settings')}
                            disabled={!canUserInteract}
                          >
                            <span className="toolbar-icon-hover">
                              <Settings2 className="h-4 w-4" />
                            </span>
                          </Button>
                        </DropdownMenuTrigger>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{t('chats.chatInputV2.settings')}</p>
                      </TooltipContent>
                    </Tooltip>

                    <DropdownMenuContent
                      align="start"
                      className="w-auto min-w-56 p-1.5"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* The dropdown only renders when shouldShowChatOptions, so no inner guard. */}
                      <div className="flex flex-wrap gap-2 p-1.5">
                        <ChatOptions
                          isLoading={optionsInternalLoading}
                          setIsLoading={setOptionsInternalLoading}
                          onOptionUpdated={updateOptions}
                          attachmentsCount={files.length}
                          attachments={files}
                          options={options}
                          onClose={() => setSettingsOpen(false)}
                          onFilePickerOpenChange={(open) => {
                            filePickerOpen.current = open;
                          }}
                        />
                      </div>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              )}
            </div>

            <div className="flex items-center gap-xs">
              {imageGeneration && (
                <ImageModelChip
                  models={imageGeneration.models}
                  value={imageModel ?? String(imageGeneration.defaults.settings.model)}
                  disabled={!canUserInteract}
                  onChange={(model) => updateOptions(pickModel(options, model as ImageModel))}
                />
              )}
              {/* Right: AI Model Selector */}
              {offeredModels && offeredModels.length > 0 && (
                <DropdownMenu>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 py-0.5 rounded-lg text-xs font-medium text-neutral-700"
                          disabled={!canUserInteract}
                        >
                          <span>
                            {selectedModel
                              ? getModelDisplayLabel(selectedModel)
                              : offeredModels[0]
                                ? getModelDisplayLabel(offeredModels[0])
                                : t('chats.chatInputV2.selectModel')}
                          </span>
                          <ChevronDown className="h-3 w-3 ml-2" />
                        </Button>
                      </DropdownMenuTrigger>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{t('chats.chatInputV2.aiModel')}</p>
                    </TooltipContent>
                  </Tooltip>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel className="p-2">
                      {t('chats.chatInputV2.aiModel')}
                    </DropdownMenuLabel>
                    {offeredModels.map((model) => {
                      // While a video is attached, only video-capable models can be
                      // selected; the rest are disabled so the user can't switch to
                      // an incompatible model.
                      const disabledForVideo = hasVideoAttachment && !isVideoCapableModel(model);
                      return (
                        <DropdownMenuItem
                          key={model}
                          disabled={disabledForVideo}
                          onClick={() => {
                            if (disabledForVideo) return;
                            handleModelChange(model);
                          }}
                        >
                          <div className="flex items-center gap-2 w-full">
                            <span className="flex-1">{getModelDisplayLabel(model)}</span>
                            {selectedModel === model && <Check className="h-4 w-4" />}
                          </div>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {effortEnabled && !AGENTS_WITH_MODEL_IN_SETTINGS.includes(currentAgentId) && (
                <EffortControl
                  model={selectedModel}
                  value={currentEffort}
                  disabled={!canUserInteract}
                  onChange={(effort) => {
                    updateOptions({
                      ...(options ?? {}),
                      llmOptions: { ...(options?.llmOptions ?? {}), effort },
                    });
                    persistTemplatesSettingsFromStore();
                  }}
                />
              )}
              {footerEnd}
            </div>
          </div>
        </div>

        {isProjectContextEnabled && (
          <Dialog open={isPickerOpen} onOpenChange={setIsPickerOpen}>
            <DialogContent
              aria-describedby={undefined}
              className="gap-0 p-4 w-full max-w-203.25 h-[850px] max-h-[90vh] flex flex-col"
            >
              <DialogHeader>
                <DialogTitle>{t('chats.chatInputV2.addContextFromProject')}</DialogTitle>
              </DialogHeader>

              <DialogBody className="flex flex-1 min-h-0 flex-col gap-4 py-4">
                <ProjectContextPicker workspaceId={workspaceId} {...pickerProps} />
              </DialogBody>

              <DialogFooter>
                <Button variant="outline" onClick={() => setIsPickerOpen(false)}>
                  {t('chats.chatInputV2.cancel')}
                </Button>
                <Button disabled={!canConfirm} onClick={handleConfirmProjectContext}>
                  {selectedCount > 0
                    ? t('chats.chatInputV2.addWithCount', { selectedCount })
                    : t('chats.chatInputV2.add')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Mounted only while open: the composer renders on every chat screen and this modal pulls in
            the legacy monks-ui Modal, which needs the theme provider. */}
        {unsavedEditsPromptOpen && (
          <UnsavedChangesModal
            open
            onCancel={cancelSendWithUnsavedEdits}
            onConfirmLeave={confirmSendWithUnsavedEdits}
            title={t('chats.chatInputV2.unsavedEdits')}
            message={t('chats.chatInputV2.youHaveUnsavedEditsInTheOpenWidget')}
            confirmLabel={t('chats.chatInputV2.sendAnyway')}
          />
        )}
      </div>
    </TooltipProvider>
  );
});

ChatInput.displayName = 'ChatInput';
